import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { Toaster } from "@/components/ui/sonner";
import Auth from "@/pages/Auth";
import Debug from "@/pages/Debug";
import AuthDebug from "@/pages/AuthDebug";
import UserDashboard from "@/pages/UserDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import NotFound from "@/pages/NotFound";

function ProtectedRoute({ children, adminOnly = false }: { children: React.ReactNode; adminOnly?: boolean }) {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user || !profile) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && profile.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Redirect authenticated users
  if (user && profile) {
    if (profile.role === 'admin') {
      return (
        <Routes>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/debug" element={<Debug />} />
          <Route path="/auth-debug" element={<AuthDebug />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      );
    } else {
      return (
        <Routes>
          <Route path="/dashboard" element={<UserDashboard />} />
          <Route path="/debug" element={<Debug />} />
          <Route path="/auth-debug" element={<AuthDebug />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      );
    }
  }

  return (
    <Routes>
      <Route path="/" element={<Auth />} />
      <Route path="/debug" element={<Debug />} />
      <Route path="/auth-debug" element={<AuthDebug />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider defaultTheme="light" storageKey="broker-theme">
        <AuthProvider>
          <LanguageProvider>
            <AppRoutes />
            <Toaster />
          </LanguageProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;